import React from 'react';
import { Play, Calendar } from 'lucide-react';
import { filmsImages } from '../mockData';
import '../styles/projectsoftpower.css';

const Films = () => {
  const filmCategories = [
    {
      title: 'International Co-Productions',
      description: 'Supporting cross-border cinema that brings diverse stories to global audiences',
      count: '250+ films'
    },
    {
      title: 'Documentary Archive',
      description: 'Preserving cultural narratives through documentary cinema',
      count: '500+ documentaries'
    },
    {
      title: 'Film Festival Network',
      description: 'Connecting 100+ international film festivals across six continents',
      count: '100+ festivals'
    },
    {
      title: 'Emerging Filmmakers',
      description: 'Supporting the next generation of cinema storytellers',
      count: '1,200+ filmmakers'
    }
  ];

  const upcomingEvents = [
    {
      title: 'Global Cinema Summit 2025',
      date: 'September 15-20, 2025',
      location: 'Berlin, Germany',
      description: 'International conference on the future of cinema and cultural exchange'
    },
    {
      title: 'Heritage Film Restoration Workshop',
      date: 'October 5-12, 2025',
      location: 'Mumbai, India',
      description: 'Training program for preserving and restoring cultural film heritage'
    },
    {
      title: 'African Cinema Week',
      date: 'November 1-7, 2025',
      location: 'Lagos, Nigeria',
      description: 'Celebrating contemporary African filmmaking and storytelling'
    }
  ];

  return (
    <div className="films-page">
      {/* Hero Section */}
      <section className="section-spacing-large" style={{
        background: 'linear-gradient(135deg, var(--color-gray-50) 0%, var(--color-white) 100%)',
        textAlign: 'center'
      }}>
        <div className="container-psp">
          <h1 className="hero-title">Film & Media</h1>
          <p className="hero-tagline" style={{ maxWidth: '800px', margin: '1.5rem auto 0' }}>
            Cinema as a universal language. Supporting international film co-productions, 
            documentaries, and festivals that tell diverse human stories.
          </p>
        </div>
      </section>

      {/* Film Gallery */}
      <section className="section-spacing-large" style={{ background: 'var(--color-gray-50)' }}>
        <div className="container-psp">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>
            <h2 className="section-title">Cinema Showcase</h2>
            <p className="body-text" style={{ marginTop: '1rem' }}>
              Behind the scenes of international cinema
            </p>
          </div>

          <div className="grid-3">
            {filmsImages.map((film, index) => (
              <div
                key={index}
                className="card-psp image-overlay"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <img
                  src={film.url}
                  alt={film.title}
                  className="card-image"
                  style={{ height: '350px' }}
                />
                <div className="overlay-content">
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'var(--color-white)',
                    color: 'var(--color-indigo)',
                    marginBottom: '1rem'
                  }}>
                    <Play size={24} fill="currentColor" />
                  </div>
                  <h3 className="card-title" style={{ color: 'var(--color-white)', marginBottom: '0.5rem' }}>
                    {film.title}
                  </h3>
                  <p className="caption-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {film.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Film Categories */}
      <section className="section-spacing-large" style={{ background: 'var(--color-white)' }}>
        <div className="container-psp">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>
            <h2 className="section-title">Our Film Programs</h2>
            <p className="body-text" style={{ marginTop: '1rem' }}>
              Comprehensive support for international cinema
            </p>
          </div>

          <div className="grid-2">
            {filmCategories.map((category, index) => (
              <div
                key={index}
                className="card-psp"
                style={{
                  background: 'var(--color-gray-50)',
                  padding: 'var(--spacing-xl)',
                  border: '1px solid var(--color-gray-200)'
                }}
              >
                <h3 className="card-title" style={{ marginBottom: '0.75rem' }}>
                  {category.title}
                </h3>
                <p className="card-description" style={{ marginBottom: '1rem' }}>
                  {category.description}
                </p>
                <span style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  background: 'var(--color-indigo)',
                  color: 'var(--color-white)',
                  borderRadius: '1rem',
                  fontSize: '0.8125rem',
                  fontWeight: '600'
                }}>
                  {category.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="section-spacing-large" style={{ background: 'var(--color-gray-50)' }}>
        <div className="container-psp">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>
            <h2 className="section-title">Upcoming Film Events</h2>
            <p className="body-text" style={{ marginTop: '1rem' }}>
              Join us at international cinema gatherings
            </p>
          </div>

          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {upcomingEvents.map((event, index) => (
              <div
                key={index}
                className="card-psp fade-in-up"
                style={{
                  background: 'var(--color-white)',
                  padding: 'var(--spacing-xl)',
                  marginBottom: 'var(--spacing-lg)',
                  animationDelay: `${index * 0.15}s`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{
                    flexShrink: 0,
                    width: '48px',
                    height: '48px',
                    borderRadius: '0.75rem',
                    background: 'var(--color-indigo)',
                    color: 'var(--color-white)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Calendar size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="subsection-title" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                      {event.title}
                    </h3>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      <span className="caption-text" style={{ fontWeight: '600', color: 'var(--color-indigo)' }}>
                        {event.date}
                      </span>
                      <span className="caption-text" style={{ color: 'var(--color-gray-600)' }}>
                        {event.location}
                      </span>
                    </div>
                    <p className="body-text">{event.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-spacing-large" style={{
        background: 'linear-gradient(135deg, var(--color-gold) 0%, var(--color-saffron) 100%)',
        color: 'var(--color-white)',
        textAlign: 'center'
      }}>
        <div className="container-psp">
          <h2 className="section-title" style={{ color: 'var(--color-white)' }}>
            Join Our Film Network
          </h2>
          <p className="body-text" style={{
            color: 'rgba(255, 255, 255, 0.95)',
            maxWidth: '600px',
            margin: '1.5rem auto 2rem'
          }}>
            Whether you're a filmmaker, festival organizer, or cinema enthusiast, connect with our global community.
          </p>
          <button className="btn-primary" style={{
            background: 'var(--color-white)',
            color: 'var(--color-gold)',
            border: '2px solid var(--color-white)'
          }}>
            Become a Partner
          </button>
        </div>
      </section>
    </div>
  );
};

export default Films;
